import { AfterViewInit, Component, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { IWeissmanModalComponent } from '../../../../../Compliance/WeissmanModalService';
import { IdpService, MappingExplanations } from '../idp.service';
import { assign, chain, find, forEach, isNumber, isUndefined, map } from 'lodash';
import { AssessmentViewModel } from 'src/app/Annual-Details/Assessments/assessment.service';
import { AssessmentComponent } from 'src/app/Annual-Details/Assessments/assessment.component';
import { FeatureFlagsService } from 'src/app/Common/FeatureFlags/feature-flags-service';

export interface IdpAssessmentDetailModalParams {
    confidence: string;
    idpResults: Weissman.Model.Workflow.DocumentIntake.Idp.Assessment.TaxAssessmentAPIResponse;
    assessmentVM: AssessmentViewModel;
    disableApplyAll: boolean;
    assessmentChild: AssessmentComponent;
    mappingExplanations: MappingExplanations;
}

export interface IdpAssessmentDetailModalResult {
    updatedAssessmentVM: AssessmentViewModel;
    applyAll: boolean;
}

type ComponentRow = {
    name: string;
    av: number;
    ratio: number;
    fmv: number;
    type: string;
    explanation?: string;
    apply?: boolean;
};

type ExemItem =
    | Weissman.Model.Workflow.DocumentIntake.Idp.Assessment.CapItem
    | Weissman.Model.Workflow.DocumentIntake.Idp.Assessment.ExemptionItem;

@Component({
    selector: 'idp-assessment-detail-modal',
    templateUrl: './idpAssessmentDetailModal.component.html',
    standalone: false,
})
export class IdpAssessmentDetailModalComponent
    implements
        OnInit,
        AfterViewInit,
        IWeissmanModalComponent<IdpAssessmentDetailModalParams, IdpAssessmentDetailModalResult>
{
    constructor(
        private readonly _bsModalRef: BsModalRef,
        private readonly _featureFlagsService: FeatureFlagsService,
        private readonly _idpService: IdpService,
    ) {}

    params: IdpAssessmentDetailModalParams;
    result: IdpAssessmentDetailModalResult;

    tableColumns = [
        {
            name: 'Component Type',
        },
        {
            name: 'Component',
        },
        {
            name: 'Assessed Value',
            isNum: true,
        },
        {
            name: 'Ratio',
            isNum: true,
        },
        {
            name: 'Market Value',
            isNum: true,
        },
    ];

    rows: ComponentRow[];
    hoveredRow: ComponentRow;
    appealDeadline: Date;
    appealDeadlineApply: boolean = false;

    private _assessmentVM: AssessmentViewModel;
    private _assessmentRevision: Weissman.Model.Workflow.DocumentIntake.Idp.Assessment.AssessmentRevisionItem;

    get title(): string {
        return `Detected Values: ${this.params?.confidence} Confidence`;
    }

    get isCheckboxesEnabled(): boolean {
        return this._featureFlagsService.featureFlags.idpModalCheckboxes;
    }

    ngOnInit(): void {
        this._assessmentVM = this.params.assessmentVM;
        this._assessmentRevision = this.params.idpResults.assessmentRevision;

        this.appealDeadline = this._assessmentRevision.appealDeadline;

        const avComponents = chain(this._assessmentRevision.assessedValue?.components ?? [])
            .filter('amount')
            .map((x) => ({
                name: x.name,
                av: x.amount,
            }))
            .value();

        const fmvComponents = chain(this._assessmentRevision.fairMarketValue?.components ?? [])
            .filter('amount')
            .map((x) => ({
                name: x.name,
                fmv: x.amount,
            }))
            .value();

        const ratioComponents = chain(this._assessmentRevision.components ?? [])
            .filter('ratio')
            .map((x) => ({
                name: x.componentName,
                ratio: x.ratio,
            }))
            .value();

        this.rows = chain([...avComponents, ...fmvComponents, ...ratioComponents])
            .groupBy('name')
            .map((vallArra, componentName) => {
                const explanation = find(
                    this.params.mappingExplanations.components,
                    (x) => x.name === componentName,
                )?.explanation;
                const comp = {
                    name: componentName,
                    type: 'Normal',
                    explanation,
                };

                forEach(vallArra, (val) => {
                    assign(comp, val);
                });

                return comp;
            })
            .value() as ComponentRow[];

        const fmvTotal = this.rows.reduce((total, comp) => (isUndefined(comp.fmv) ? null : (total += comp.fmv)), 0);
        const avTotal = this.rows.reduce((total, comp) => (isUndefined(comp.av) ? null : (total += comp.av)), 0);

        const totalRow = {
            type: 'Total',
            name: '',
            ratio: null,
            fmv: fmvTotal || null,
            av: avTotal || null,
        } as ComponentRow;

        if (this._assessmentRevision.assessedValue?.amount) {
            if (this._assessmentRevision.assessedValue?.components.length === 0) {
                totalRow.av = this._assessmentRevision.assessedValue.amount;
                totalRow.ratio = this._assessmentRevision.assessedValue.ratio;
                totalRow.explanation = this.params.mappingExplanations.av;
            }
        } else if (this.params.idpResults.taxAuthorities[0]?.assessedValueAmount) {
            totalRow.av = this.params.idpResults.taxAuthorities[0].assessedValueAmount;
            totalRow.explanation = this.params.mappingExplanations.av;
        }

        if (
            this._assessmentRevision.fairMarketValue?.components.length === 0 &&
            this._assessmentRevision.fairMarketValue?.amount
        ) {
            totalRow.fmv = this._assessmentRevision.fairMarketValue.amount;

            // In the case that there's both an FMV and AV amount, we want to keep the AV explanation, since the FMV was actually skipped during the mapping process
            totalRow.explanation = totalRow.explanation || this.params.mappingExplanations.fairMarketValue;
            totalRow.ratio = totalRow.ratio || this._assessmentRevision.fairMarketValue.ratio;
        }

        this.rows.push(totalRow);

        const exemRows = this._getExtraRows(this._assessmentRevision.exemptions[0], 'Exemptions', 'Exem');

        if (exemRows.length === 0 && this.params.idpResults.taxAuthorities[0]?.exemptionAmount) {
            exemRows.push({
                type: 'Exemptions',
                name: 'Exem',
                av: -this.params.idpResults.taxAuthorities[0].exemptionAmount,
                ratio: null,
                fmv: null,
                explanation: this.params.mappingExplanations.exem,
            });
        }

        const exemTotal = exemRows.reduce((total, comp) => (isUndefined(comp.av) ? null : (total += comp.av)), 0);

        if (exemTotal) {
            this.rows[this.rows.length - 1].name = 'Pre Adj Total';

            const exemTotalRow = {
                type: 'Total',
                name: '',
                ratio: null,
                fmv: null,
                av: totalRow.av + exemTotal,
            };
            exemRows.push(exemTotalRow);
        }

        const capRows = this._getExtraRows(this._assessmentRevision.cap, 'Alternate', 'Cap AV');

        this.rows = [...this.rows, ...exemRows, ...capRows];
    }

    ngAfterViewInit(): void {
        const modalContainer = document.querySelector('modal-container');
        if (modalContainer) {
            modalContainer.classList.add('no-modal-backdrop');
        }
    }

    get atLeastOneSelected(): boolean {
        return this.rows.some((row) => row.apply) || this.appealDeadlineApply;
    }

    async apply(applyAll?: boolean): Promise<void> {
        await this._idpService.mapAssessmentVMFromIDP(
            this._assessmentVM,
            this._assessmentRevision,
            this.params.idpResults.taxAuthorities[0],
            this.params.assessmentChild,
            false,
            true,
        );

        this.result = {
            updatedAssessmentVM: this._assessmentVM,
            applyAll: applyAll ?? false,
        };

        this._bsModalRef.hide();
    }

    applySome(): void {
        if (this._assessmentRevision.assessedValue?.components) {
            this._assessmentRevision.assessedValue.components =
                this._assessmentRevision.assessedValue.components.filter((comp) => {
                    const row = this.rows.find((r) => r.name === comp.name);
                    return row?.apply;
                });
        }

        if (this._assessmentRevision.fairMarketValue?.components) {
            this._assessmentRevision.fairMarketValue.components =
                this._assessmentRevision.fairMarketValue.components.filter((comp) => {
                    const row = this.rows.find((r) => r.name === comp.name);
                    return row?.apply;
                });
        }

        this._assessmentRevision.appealDeadline = this.appealDeadlineApply ? this.appealDeadline : null;

        this.apply();
    }

    cancel(): void {
        this._bsModalRef.hide();
    }

    private _getExtraRows(exemItem: ExemItem, type: string, name: string): ComponentRow[] {
        const exemRows = chain(exemItem?.components ?? [])
            .filter('amount')
            .map((x) => {
                const explanation = find(
                    this.params.mappingExplanations.components,
                    (c) => c.name === x.name,
                )?.explanation;

                return {
                    type,
                    name: x.name,
                    av: type === 'Exemptions' ? -x.amount : x.amount,
                    ratio: null,
                    fmv: null,
                    explanation,
                };
            })
            .value();

        if (exemItem?.components.length === 0 && exemItem?.amount) {
            exemRows.push({
                type,
                name,
                av: type === 'Exemptions' ? -exemItem.amount : exemItem.amount,
                ratio: null,
                fmv: null,
                explanation:
                    type === 'Exemptions' ? this.params.mappingExplanations.exem : this.params.mappingExplanations.cap,
            });
        }

        return exemRows;
    }
}
